import { useApolloClient, useQuery } from '@apollo/client';
import format from 'date-fns/format';
import eruda from 'eruda';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { redirectToAuth } from 'supertokens-auth-react';
import Session from 'supertokens-auth-react/recipe/session';

import {
  Caption,
  DropdownMenu,
  DropdownMenuAnchorItem,
  DropdownMenuContent,
  DropdownMenuCustomItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuLinkItem,
  DropdownMenuSelectableItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Icon,
  NewButton,
  Paragraph,
  SidebarMenuCoreButton,
  TenantCard,
  VStack,
  useSidebar,
} from '@eluve/components';
import { clearSkippedInterstitials } from '@eluve/interstitials';
import { usePostHog } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useImpersonation,
  useImpersonationTimeRemaining,
  useIsTenantAdminFromSession,
  useSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { SystemStatusDialog } from '@eluve/site-banners';
import {
  useIsFeatureFlagEnabled,
  useTenantLogo,
  useTenantName,
  useUserName,
} from '@eluve/smart-blocks';
import { useDialog } from '@eluve/utility-hooks';

import { appConfig } from '../../../../config';
import { GetTenantsList } from '../operations';

const DEVELOPER_CLICK_THRESHOLD = 5;
const DEVELOPER_CLICK_TIMEOUT_MS = 2000;
const DROPDOWN_OFFSET_AND_PADDING = 8;
const TENANT_LIST_LIMIT = 10;

export const UserDropdownMenu = () => {
  const [clickCount, setClickCount] = useState(0);

  const canManageLocalFiles = useIsFeatureFlagEnabled('LOCAL_FILES_MANAGEMENT');
  const client = useApolloClient();
  const isTenantAdmin = useIsTenantAdminFromSession();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const tenantId = useAssignedTenantIdFromParams();
  const tenantName = useTenantName(tenantId);
  const userId = useUserIdFromSession();

  const { data } = useQuery(GetTenantsList);
  const { isMobile, open: isOpen } = useSidebar();
  const session = useSession();
  const { isEluveAdmin, isPromptTemplateCreator, isEluveEvalUser, isReviewer } =
    session;

  const {
    isImpersonating,
    isLoading: isImpersonationLoading,
    exitImpersonation,
    expiresAt: impersonationExpiresAt,
    impersonationOriginUrl,
  } = useImpersonation();

  const { tenantLogoUrl } = useTenantLogo(tenantId);

  const {
    isDialogOpen: systemStatusDialogOpen,
    toggleDialog: toggleSystemStatusDialog,
  } = useDialog();

  const date = format(Date.now(), 'yyyy-MM-dd');
  const isCollapsed = !isOpen && !isMobile;
  const tenants = (data?.assignedTenantUsers ?? []).filter(
    (tenant) => tenant.tenantId != null,
  );

  let timeout: NodeJS.Timeout;
  const handleDeveloperClick = () => {
    setClickCount((count) => count + 1);
    clearTimeout(timeout);

    if (clickCount > DEVELOPER_CLICK_THRESHOLD) {
      setClickCount(0);
      eruda.init();
    } else {
      timeout = setTimeout(() => setClickCount(0), DEVELOPER_CLICK_TIMEOUT_MS);
    }
  };

  const handleLogoutClick = async () => {
    clearSkippedInterstitials();
    posthog?.reset();
    await Session.signOut();
    redirectToAuth();
  };

  const handleTenantClick = (newTenantId: string) => {
    client.resetStore();
    navigate(`/organizations/switch/${newTenantId}`);
  };

  const userDisplayName = useUserName(userId, '');

  // Get auto-updating time remaining (updates every minute, long format)
  const timeRemaining = useImpersonationTimeRemaining(
    impersonationExpiresAt,
    'long',
  );

  return (
    <DropdownMenu modal={!isMobile}>
      <DropdownMenuTrigger asChild>
        <SidebarMenuCoreButton className="relative mr-auto mt-auto" size="lg">
          <TenantCard
            icon={{ name: 'ChevronsUpDown' }}
            showBadge={isImpersonating}
            tenantLogoUrl={tenantLogoUrl}
            tenantName={tenantName}
            userDisplayName={userDisplayName}
          />
        </SidebarMenuCoreButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        alignOffset={isCollapsed ? 0 : DROPDOWN_OFFSET_AND_PADDING}
        className="min-w-[200px]"
        onCloseAutoFocus={(event) => event.preventDefault()}
        sideOffset={isCollapsed ? DROPDOWN_OFFSET_AND_PADDING : 0}
      >
        {isImpersonating && (
          <>
            <DropdownMenuCustomItem>
              <VStack
                align="center"
                className="pointer-events-auto rounded-md border border-border-warning-subtle bg-bg-warning-subtle p-2 text-text-warning-subtle [&>*:last-child]:mt-2"
                gap={1}
              >
                <Icon name="AlertTriangle" size="xs" />
                <Paragraph color="inherit" size="sm">
                  Impersonating User
                </Paragraph>
                {timeRemaining && (
                  <Caption className="opacity-60" color="inherit" size="sm">
                    {timeRemaining}
                  </Caption>
                )}
                <NewButton
                  loading={isImpersonationLoading}
                  onClick={() => exitImpersonation(impersonationOriginUrl)}
                  size="s"
                  text="Exit Impersonation"
                  wFull
                />
              </VStack>
            </DropdownMenuCustomItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuCustomItem>
          <TenantCard
            tenantName={tenantName}
            userDisplayName={userDisplayName}
            tenantLogoUrl={tenantLogoUrl}
          />
        </DropdownMenuCustomItem>
        {tenants.length > 1 && tenants.length <= TENANT_LIST_LIMIT && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Switch Organization</DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              collisionPadding={DROPDOWN_OFFSET_AND_PADDING}
            >
              {tenants.map((tenant) => (
                <DropdownMenuSelectableItem
                  currentValue={tenantId}
                  key={tenant.tenantId}
                  onClick={handleTenantClick}
                  text={tenant.name ?? 'Unnamed Organization'}
                  value={tenant.tenantId as string}
                />
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {tenants.length > 1 && tenants.length > TENANT_LIST_LIMIT && (
          <DropdownMenuLinkItem
            text="Switch Organization"
            to={`/organizations`}
          />
        )}
        {isTenantAdmin && (
          <DropdownMenuLinkItem
            text="Organization Settings"
            to={`/organizations/${tenantId}/admin`}
          />
        )}
        {(canManageLocalFiles || isPromptTemplateCreator) && (
          <DropdownMenuSeparator />
        )}
        {(canManageLocalFiles || isPromptTemplateCreator) && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Eluve Tools</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {isPromptTemplateCreator && (
                <DropdownMenuLinkItem
                  text="Create Prompt Template"
                  to="/prompts"
                />
              )}
              {isPromptTemplateCreator && (
                <DropdownMenuLinkItem
                  text="Output Templates"
                  to="/output-templates"
                />
              )}
              {canManageLocalFiles && (
                <DropdownMenuLinkItem
                  text="Manage Device Files"
                  to={`/organizations/${tenantId}/files`}
                />
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {(isEluveAdmin || isEluveEvalUser || isReviewer) && (
          <DropdownMenuSeparator />
        )}
        {isEluveAdmin && (
          <DropdownMenuLinkItem text="Admin Dashboard" to="/admin" />
        )}
        {isEluveEvalUser && (
          <DropdownMenuLinkItem
            text="Eval Dashboard"
            to="/eval/source-artifact-review"
          />
        )}
        {isReviewer && (
          <DropdownMenuLinkItem text="Review Dashboard" to="/review" />
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={toggleSystemStatusDialog}>
          System Status
        </DropdownMenuItem>
        <DropdownMenuAnchorItem
          href={`mailto:feedback@eluve.com?subject=Eluve Feedback - ${date}`}
          text="Contact Us"
        />
        <DropdownMenuItem onClick={handleLogoutClick}>Log Out</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel onClick={handleDeveloperClick}>
          {appConfig.VITE_APP_VERSION}
        </DropdownMenuLabel>
      </DropdownMenuContent>
      <SystemStatusDialog
        open={systemStatusDialogOpen}
        toggleOpen={toggleSystemStatusDialog}
      />
    </DropdownMenu>
  );
};
